import { useEffect, useMemo, useState, useRef } from "react";
import { ActionIcon, Center, Flex, Loader, Text, Title, Badge, Tooltip, useMantineTheme } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import { useDebouncedValue } from "@mantine/hooks";
import { IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { formatDate } from "@/shared/utils/format.utils";
import { OrderTypeBadge } from "@/pages/admin/AdminStatusBadge";

type ReconciliationRecord = {
  id: string;
  folio_no: string;
  orderId: string | null;
  userId: string | null;
  userName: string | null;
  orderType: string | null;
  purprice: number | null;
  units: number | null;
  amount: number | null;
  stamp_duty: number | null;
  notes: string | null;
  source: string | null;
  createdAt: string;
};

const PAGE_SIZE = 100;
const SOURCE_OPTIONS = [
  { value: "CAMS", label: "CAMS" },
  { value: "KFIN", label: "KFIN" },
];

export default function ReconciliationHistoryPage() {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [records, setRecords] = useState<ReconciliationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);

  const previousParamsRef = useRef<string>("");

  const filter = useMemo(() => {
    const f: Record<string, unknown> = {};
    for (const col of debouncedFilters) {
      if (col.value === undefined || col.value === null || col.value === "") continue;
      if (col.id === "createdAt" && Array.isArray(col.value)) {
        const [from, to] = col.value as [Date | null, Date | null];
        if (from) f.createdAtFrom = from;
        if (to) f.createdAtTo = to;
      } else {
        f[col.id] = col.value;
      }
    }
    return Object.keys(f).length > 0 ? f : undefined;
  }, [debouncedFilters]);

  const sortingInput = useMemo(
    () => (sorting.length > 0 ? sorting.map((s) => ({ id: s.id, desc: s.desc })) : undefined),
    [sorting]
  );

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sortingInput,
      filter,
    };
    const paramsString = JSON.stringify(params);
    if (paramsString === previousParamsRef.current) return;
    previousParamsRef.current = paramsString;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await trpc.reconciliation.list.query(params as any);
        setRecords(res.data.records as any);
        setTotalRows(res.data.meta.total);
      } catch (err) {
        handleTrpcError(err);
        setRecords([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pagination.pageIndex, pagination.pageSize, filter, sortingInput]);

  const columns: MRT_ColumnDef<ReconciliationRecord>[] = useMemo(
    () => [
      {
        accessorKey: "folio_no",
        header: "Folio No",
        size: 150,
        enableSorting: true,
      },
      {
        accessorKey: "orderId",
        header: "Order ID",
        size: 160,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "userName",
        header: "User Name",
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() || "—",
      },
      {
        accessorKey: "orderType",
        header: "Order Type",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <OrderTypeBadge type={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "source",
        header: "Source",
        size: 120,
        filterVariant: "select",
        mantineFilterSelectProps: { data: SOURCE_OPTIONS, clearable: true },
        enableSorting: true,
        Cell: ({ cell }) => {
          const val = cell.getValue<string | null>();
          if (!val) return "—";
          return (
            <Badge color={val === "CAMS" ? "blue" : "green"} variant="light">
              {val}
            </Badge>
          );
        },
      },
      {
        accessorKey: "purprice",
        header: "NAV / Price",
        size: 120,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<number | null>()?.toFixed(4) ?? "—",
      },
      {
        accessorKey: "units",
        header: "Units",
        size: 120,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<number | null>()?.toFixed(4) ?? "—",
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 130,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => {
          const val = cell.getValue<number | null>();
          return val != null ? `₹ ${val.toLocaleString("en-IN")}` : "—";
        },
      },
      {
        accessorKey: "stamp_duty",
        header: "Stamp Duty",
        size: 120,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<number | null>()?.toFixed(2) ?? "—",
      },
      {
        accessorKey: "notes",
        header: "Notes",
        size: 350,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Text fz="xs" lineClamp={2} style={{ whiteSpace: "pre-wrap" }}>
            {cell.getValue<string | null>() ?? "—"}
          </Text>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 200,
        filterVariant: "date-range",
        enableSorting: true,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
    ],
    []
  );

  if (loading && records.length === 0) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={2} fw={700}>
          Reconciliation History
        </Title>
      </Flex>

      <MantineReactTable
        columns={columns}
        data={records}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
        }}
        positionActionsColumn="last"
        mantinePaginationProps={{
          rowsPerPageOptions: ["10", "25", "50", "100"],
        }}
        paginationDisplayMode="pages"
        renderRowActions={({ row }) => (
          <Tooltip label={row.original.userId ? "View User" : "User not available"}>
            <ActionIcon
              c="slate.6"
              variant="transparent"
              disabled={!row.original.userId}
              onClick={() => row.original.userId && navigate(`/users/${row.original.userId}`)}
            >
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        initialState={{ density: "xs", columnPinning: { right: ["mrt-row-actions"] } }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 200px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "2rem" }}>
              No reconciliation records found.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
